import { useState, useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { useQuery, useMutation, useQueryClient } from 'react-query'
import { useParams, useNavigate } from 'react-router-dom'
import { productsAPI } from '../services/productsAPI'
import Card, { CardHeader, CardTitle, CardContent } from '../components/UI/Card'
import Button from '../components/UI/Button'
import LoadingSpinner from '../components/UI/LoadingSpinner'
import Select from '../components/UI/Select'
import { ArrowLeft, Save, X, Package, Upload, Trash2 } from 'lucide-react'
import toast from 'react-hot-toast'

export default function EditProduct() {
  const { id } = useParams()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [imagePreview, setImagePreview] = useState(null)
  const [selectedImage, setSelectedImage] = useState(null)

  const { data, isLoading } = useQuery(['product', id], () => productsAPI.getById(id))
  const product = data?.data?.data?.product || null

  // Check if product has active subscriptions (cannot set to Inactive when true)
  const { data: deletionCheck } = useQuery(
    ['product-check-deletion', id],
    () => productsAPI.checkDeletion(id),
    { enabled: !!id && !!product }
  )
  const hasActiveSubscriptions = deletionCheck?.data?.canDelete === false

  const { register, handleSubmit, reset, formState: { errors }, setValue, watch } = useForm({
    defaultValues: {
      name: '',
      description: '',
      status: 'active',
      url: '',
      category: ''
    }
  })

  // Reset form when product data loads
  useEffect(() => {
    if (product) {
      const status = product.status || 'active'
      reset({
        name: product.name || '',
        description: product.description || '',
        status,
        url: product.url || '',
        category: product.category || ''
      })
      // Set image preview if product has an image
      if (product.image_url) {
        setImagePreview(product.image_url)
      } else {
        setImagePreview(null)
      }
      setSelectedImage(null)
    }
  }, [product, reset])

  const currentStatus = watch('status')
  const showInactiveWarning = hasActiveSubscriptions && currentStatus === 'inactive'
  const canSave = !showInactiveWarning

  const mutation = useMutation(({ id, payload }) => productsAPI.update(id, payload), {
    onSuccess: async () => {
      queryClient.invalidateQueries(['product', id])
      queryClient.invalidateQueries(['products'])
      await queryClient.refetchQueries(['products'])
      toast.success('Product updated successfully!')
      navigate(`/products/${id}`)
    },
    onError: (error) => {
      console.error('Error updating product:', error)
      toast.error(error.response?.data?.message || 'Failed to update product')
    }
  })

  const handleImageChange = (e) => {
    const file = e.target.files?.[0]
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        toast.error('Please select a valid image file')
        return
      }
      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        toast.error('Image size should be less than 5MB')
        return
      }
      setSelectedImage(file)
      const reader = new FileReader()
      reader.onloadend = () => {
        setImagePreview(reader.result)
      }
      reader.readAsDataURL(file)
    }
  }

  const removeImage = () => {
    setSelectedImage(null)
    setImagePreview(null)
    setValue('image', null)
  }

  const onSubmit = async (data) => {
    setIsSubmitting(true)
    try {
      // Create FormData if image is being uploaded, otherwise use regular JSON
      if (selectedImage) {
        const formData = new FormData()
        formData.append('name', data.name)
        formData.append('description', data.description || '')
        formData.append('status', data.status)
        formData.append('url', data.url || '')
        formData.append('category', data.category || '')
        formData.append('image', selectedImage)
        await mutation.mutateAsync({ id, payload: formData })
      } else {
        // Regular JSON update without image
        await mutation.mutateAsync({ id, payload: data })
      }
    } catch (error) {
      // Error handled in mutation onError
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleCancel = () => {
    navigate(`/products/${id}`)
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  if (!product) {
    return (
      <div className="text-center py-12">
        <h2 className="text-2xl font-bold text-secondary-900 mb-4">Product Not Found</h2>
        <p className="text-secondary-600 mb-4">The product you're trying to edit doesn't exist.</p>
        <Button onClick={() => navigate('/products')}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Products
        </Button>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center space-x-4">
          <Button
            variant="outline"
            onClick={handleCancel}
            className="flex items-center"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-secondary-900">Edit Product</h1>
            <p className="text-secondary-600 mt-1">Update product information and pricing</p>
          </div>
        </div>
        <div className="mt-4 sm:mt-0 flex items-center space-x-3">
          <Button
            variant="outline"
            onClick={handleCancel}
            disabled={isSubmitting}
          >
            <X className="h-4 w-4 mr-2" />
            Cancel
          </Button>
          <Button
            onClick={handleSubmit(onSubmit)}
            disabled={isSubmitting || !canSave}
            className="min-w-[100px]"
            title={showInactiveWarning ? 'Cannot save: product has active organization subscriptions' : undefined}
          >
            {isSubmitting ? (
              <LoadingSpinner size="sm" />
            ) : (
              <>
                <Save className="h-4 w-4 mr-2" />
                Save Changes
              </>
            )}
          </Button>
        </div>
      </div>

      {/* Edit Form */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <div className="h-8 w-8 rounded-lg bg-primary-100 flex items-center justify-center mr-3">
              <Package className="h-4 w-4 text-primary-600" />
            </div>
            Product Information
          </CardTitle>
        </CardHeader>

        <CardContent>
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            {/* Product Name */}
            <div>
              <label className="block text-sm font-medium text-secondary-700 mb-2">
                Product Name *
              </label>
              <input
                {...register('name', { 
                  required: 'Product name is required',
                  minLength: { value: 2, message: 'Name must be at least 2 characters' }
                })}
                type="text"
                className="input w-full"
                placeholder="Enter product name"
                disabled={isSubmitting}
              />
              {errors.name && (
                <p className="mt-1 text-sm text-red-600">{errors.name.message}</p>
              )}
            </div>

            {/* Description */}
            <div>
              <label className="block text-sm font-medium text-secondary-700 mb-2">
                Description
              </label>
              <textarea
                {...register('description')}
                rows={4}
                className="input w-full resize-none"
                placeholder="Enter product description"
                disabled={isSubmitting}
              />
            </div>

            {/* Status */}
            <div>
              <label className="block text-sm font-medium text-secondary-700 mb-2">
                Status
              </label>
              <select
                {...register('status')}
                className="input w-full"
                disabled={isSubmitting}
              >
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
                <option value="draft">Draft</option>
              </select>
              {showInactiveWarning && (
                <p className="mt-2 text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
                  This product is assigned to one or more organizations with active subscriptions. You cannot set status to Inactive until those subscriptions are removed or cancelled.
                </p>
              )}
            </div>

            {/* Image Upload */}
            <div>
              <label className="block text-sm font-medium text-secondary-700 mb-2">
                Product Image
              </label>
              {imagePreview ? (
                <div className="relative inline-block">
                  <img
                    src={imagePreview}
                    alt="Product preview"
                    className="w-32 h-32 object-cover rounded-lg border border-secondary-200"
                  />
                  <button
                    type="button"
                    onClick={removeImage}
                    className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              ) : (
                <label
                  htmlFor="image-upload"
                  className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-secondary-300 rounded-lg cursor-pointer hover:bg-secondary-50"
                >
                  <Upload className="h-8 w-8 text-secondary-400 mb-2" />
                  <span className="text-sm text-secondary-600">Click to upload image</span>
                  <input
                    id="image-upload"
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={handleImageChange}
                    disabled={isSubmitting}
                  />
                </label>
              )}
            </div>

            {/* Category */}
            <div>
              <label className="block text-sm font-medium text-secondary-700 mb-2">
                Category
              </label>
              <Select
                {...register('category')}
                className="input w-full"
                disabled={isSubmitting}
              >
                <option value="">Select a category</option>
                <option value="Banking & Financial">Banking & Financial</option>
                <option value="Technology">Technology</option>
                <option value="ERP">ERP</option>
                <option value="ITSM">ITSM</option>
                <option value="Insurance">Insurance</option>
                <option value="Automotive">Automotive</option>
                <option value="Sales & Marketing">Sales & Marketing</option>
                <option value="Lifestyle">Lifestyle</option>
                <option value="Other">Other</option>
              </Select>
            </div>

            {/* URL */}
            <div>
              <label className="block text-sm font-medium text-secondary-700 mb-2">
                URL
              </label>
              <input
                {...register('url', {
                  pattern: {
                    value: /^(https?:\/\/)?([\da-z\.-]+)\.([a-z\.]{2,6})([\/\w \.-]*)*\/?$/,
                    message: 'Please enter a valid URL'
                  }
                })}
                type="url"
                className="input w-full"
                placeholder="https://example.com"
                disabled={isSubmitting}
              />
              {errors.url && (
                <p className="mt-1 text-sm text-red-600">{errors.url.message}</p>
              )}
            </div>

            {/* Action Buttons */}
            <div className="flex items-center justify-end space-x-3 pt-4 border-t border-secondary-200">
              <Button
                type="button"
                variant="outline"
                onClick={handleCancel}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={isSubmitting || !canSave}
                className="min-w-[120px]"
                title={showInactiveWarning ? 'Cannot save: product has active organization subscriptions' : undefined}
              >
                {isSubmitting ? (
                  <LoadingSpinner size="sm" />
                ) : (
                  'Save Changes'
                )}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  )
}



